import type { ZodIssue } from 'zod';

import type { ValidationError } from '@/sections/insights/queryBuilder/components/QueryDisplay/EditableQueryJSON';
import { GraphQuerySchema } from '@/sections/insights/queryBuilder/utils/graphQuerySchema';

/**
 * Validates JSON syntax and returns errors with line numbers
 *
 * @param jsonString - The JSON string to validate
 * @returns Array of validation errors (empty if valid)
 */
export function validateJSONSyntax(jsonString: string): ValidationError[] {
  try {
    JSON.parse(jsonString);
    return []; // No errors
  } catch (error) {
    if (error instanceof SyntaxError) {
      const line = extractLineNumber(jsonString, error);
      return [
        {
          line,
          message: error.message,
          type: 'syntax',
        },
      ];
    }
    return [];
  }
}

/**
 * Extracts line number from JSON.parse SyntaxError
 *
 * Error messages contain "position N" where N is character offset.
 * Convert character position to line number by counting newlines.
 *
 * @param jsonString - The JSON string that failed to parse
 * @param error - The SyntaxError thrown by JSON.parse
 * @returns Line number (1-indexed) where error occurred
 */
function extractLineNumber(jsonString: string, error: SyntaxError): number {
  // Extract position from error message (e.g., "position 42")
  const match = error.message.match(/line (\d+)/);
  if (!match) {
    return 1; // Fallback to first line if no position found
  }

  return parseInt(match[1], 10);
}

/**
 * Validates GraphQuery schema using Zod and returns errors with line numbers
 *
 * @param jsonString - The JSON string for line number mapping
 * @param parsedJSON - The parsed JSON object to validate
 * @returns Array of validation errors (empty if valid)
 */
export function validateGraphQuerySchema(
  jsonString: string,
  parsedJSON: unknown
): ValidationError[] {
  const result = GraphQuerySchema.safeParse(parsedJSON);

  if (result.success) {
    return []; // No errors
  }

  // Map Zod errors to ValidationError[] with line numbers
  const errors = result.error.issues.map(issue => ({
    line: findLineForZodPath(jsonString, issue),
    message: formatZodError(issue),
    type: 'type' as const,
  }));

  // Filter redundant errors
  // Example: "asd" instead of "node" generates both:
  //   1. "node: Expected object, received undefined" (missing required field)
  //   2. "Unknown field(s): asd" (unrecognized key)
  // Only show error #2 since it's more actionable (tells user what to rename)
  const hasUnrecognizedKeys = errors.some(e =>
    e.message.includes('Unknown field')
  );
  const filteredErrors = hasUnrecognizedKeys
    ? errors.filter(e => {
        // Keep unrecognized key errors
        if (e.message.includes('Unknown field')) return true;
        // Filter out "received undefined" errors (likely cascade from unrecognized keys)
        if (e.message.includes('received undefined')) return false;
        // Keep other errors
        return true;
      })
    : errors;

  return filteredErrors;
}

/**
 * Finds line number for a Zod error in JSON string
 *
 * Handles different Zod error types:
 * - unrecognized_keys: Search for the typo field name from issue.keys
 * - invalid_type: Search for the field name from issue.path
 * - Other: Search for field from path
 *
 * @param jsonString - The JSON string to search
 * @param issue - Zod validation issue with code, path, and metadata
 * @returns Line number (1-indexed) where field is located, or 1 if not found
 */
function findLineForZodPath(jsonString: string, issue: ZodIssue): number {
  let searchKey: string | undefined;

  // Handle unrecognized keys (typos like "nod" instead of "node")
  if (issue.code === 'unrecognized_keys' && 'keys' in issue) {
    // Search for the actual typo field name
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const unrecognizedKeys = (issue as any).keys as string[];
    if (unrecognizedKeys && unrecognizedKeys.length > 0) {
      searchKey = unrecognizedKeys[0]; // Use first unrecognized key
    }
  }

  // For other error types, use the last key from path
  if (!searchKey) {
    const path = issue.path as (string | number)[];
    if (path.length === 0) {
      return 1; // Root level error
    }

    // Get last string key from path
    const lastKey = path.filter(segment => typeof segment === 'string').pop();
    if (!lastKey) {
      return 1; // No string keys in path (array indices only)
    }
    searchKey = lastKey;
  }

  // Search for the field name in JSON string
  // Pattern: "fieldName" with quotes
  const searchPattern = `"${searchKey}"`;
  const index = jsonString.indexOf(searchPattern);

  if (index === -1) {
    // Field not found - might be missing required field
    return 1; // Default to line 1
  }

  // Count newlines up to this position
  const textUpToField = jsonString.substring(0, index);
  const lineNumber = textUpToField.split('\n').length;

  return lineNumber;
}

/**
 * Formats Zod error into user-friendly message
 *
 * @param issue - Zod validation issue
 * @returns Human-readable error message
 */
function formatZodError(issue: ZodIssue): string {
  const path = issue.path.join('.');
  const pathPrefix = path ? `${path}: ` : '';

  // Handle common Zod error codes
  if (issue.code === 'invalid_type') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const invalidTypeIssue = issue as any;
    return `${pathPrefix}Expected ${invalidTypeIssue.expected}, received ${invalidTypeIssue.received}`;
  }

  if (issue.code === 'unrecognized_keys' && 'keys' in issue) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return `${pathPrefix}Unknown field(s): ${(issue as any).keys.join(', ')}`;
  }

  // Default: use the message from the issue
  return `${pathPrefix}${issue.message}`;
}
